<script lang="ts">
import { computed } from 'vue';

export interface PhxsgHeadingProps {
  use?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  variant?: string;
  size?: string;
  align?: 'start' | 'center' | 'end' | 'justify';
  wrap?: 'wrap' | 'wrap-nowrap' | 'wrap-balance' | 'wrap-pretty';
  truncate?: boolean;
  lineClamp?: number;
  uppercase?: boolean;
}
</script>

<script setup lang="ts">
const {
  use = 'h2',
  variant = null,
  size = null,
  align = null,
  wrap = null,
  truncate = false,
  lineClamp = null,
  uppercase = false,
} = defineProps<PhxsgHeadingProps>();

const computedVariant = computed(() => {
  if (variant) {
    return variant;
  }

  return ['h1', 'h2', 'h3'].includes(use) ? 'display' : 'headline';
});

const computedSize = computed(() => {
  if (size) {
    return size;
  }

  const sizeMap: Record<string, string> = {
    h1: 'large',
    h2: 'medium',
    h3: 'small',
    h4: 'large',
    h5: 'medium',
    h6: 'small',
  };

  return sizeMap[use] || 'medium';
});

const rootClasses = computed(() => {
  return [
    'phxsg-text',
    'phxsg-heading',
    {
      [`phxsg-text--${computedVariant.value}-${computedSize.value}`]: computedVariant.value && computedSize.value,
      [`phxsg-text--align-${align}`]: align,
      'phxsg-text--truncate': truncate,
      [`phxsg-text-${wrap}`]: wrap,
      'phxsg-text--uppercase': uppercase,
    },
  ];
});

const rootStyles = computed(() => {
  const styles: Record<string, string | number> = {};

  if (lineClamp !== null && lineClamp > 0) {
    styles['--this-text-line-clamp'] = lineClamp;
  }

  return styles;
});
</script>

<template>
  <component :is="use" :class="rootClasses" :style="rootStyles">
    <template v-if="truncate">
      <span class="phxsg-text__ellipsis-wrapper">
        <slot />
      </span>
    </template>

    <template v-else>
      <slot />
    </template>
  </component>
</template>

<style src="../Text/text.css" />
